<template>
  <div id="cartAccessories">
    <div class="accessories_box p-3" v-if="isCartAccessoryBundleEnabled">
      <div class="accessories_box_header" @click="accessoriesBoxToggle">
        <div class="row align-items-center justify-content-between">
          <div class="col">
            <b>{{ accessoryTitle }}</b>
          </div>
          <div class="col text-right">
            <i :class="accessoriesToggleIconClass"></i>
          </div>
        </div>
      </div>

      <div
        class="accessories_box_body"
        :style="{ display: isExpanded ? 'block' : 'none' }"
      >
        <Dropdown
          :options="CartAccessories"
          @update:modelValue="setAccessoryType"
          :key="selectAccessoryKey"
          data-test-id="cart-accessory-type"
          :class-name="dropdownLabelClass"
          :placeholder="accessoryTypePlaceholder"
        />

        <Transition name="list">
          <InputFieldPrice
            :label-name="accessoryPriceLabel"
            v-if="showAccessoryPrice"
            ref="accessoryPriceField"
            :disabled="disableAccessoryPricing"
            @price-emit="setAccessoryPrice"
            :inputValue="cartAccessoryPrice"
            :visible="false"
            :currency="currency"
            data-test-id="cart-accessory-price"
          />
        </Transition>
        <p v-if="accessoryErrorMessage" class="error-msg">
          {{ accessoryErrorMessage }}
        </p>

        <button
          v-if="accessoryRemovable"
          class="m-2 btn-sm remove-accessory-btn"
          @click="setAccessoryRemoved"
        >
          <i class="fa fa-trash"></i>
          {{ removeLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Dropdown from "@/apps/merchant3/shared-components/ui/Dropdown.vue";
import InputFieldPrice from "@/apps/merchant3/shared-components/input-fields/InputFieldPrice.vue";
import { computed, onMounted, Ref, ref } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useStore } from "vuex";
import useFeatureFlag from "@/apps/merchant3/hooks/useFeatureFlag";
import AvailableFeatureFlags from "@/apps/merchant3/types/AvailableFeatureFlags";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { IOptionDropdown } from "@/types/dropdown";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";
import {
  CartAccessory,
  ICartAccessory,
} from "@/apps/merchant3/types/CartAccessories";
import useCurrency from "@/apps/merchant3/shared-hooks/useCurrency";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import { isPhilippinesSaleFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import { watchEffect } from "vue";
const { t } = useI18n();
const store = useStore();
const { getCurrency } = useCurrency();
const emit = defineEmits(["accessory-update"]);
const showAccessoryPrice = ref(true);
const props = defineProps({
  devicePrice: {
    type: Number,
  },
});

const currency = computed(() => {
  return getCurrency(store.state.merchant?.merchant?.country);
});
const isCartAccessoryBundleEnabled = computed(() => {
  const { isFeatureFlagSet } = useFeatureFlag();
  const merchantEnabled = store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.CART_ACCESSORY_BUNDLE
  );

  return (
    isFeatureFlagSet(
      AvailableFeatureFlags.CART_ACCESSORY_BUNDLE,
      merchantEnabled
    ) && merchantEnabled
  );
});

// TODO: replace with service to fetch info from DB when experiment is over
// @martin.carrera
const CartAccessories = computed(() => {
  if (isPhilippinesSaleFlow()) {
    return [CartAccessory.DATA_BUNDLE];
  }
  return [
    CartAccessory.BASE_OR_SUPPORT,
    CartAccessory.SMART_WATCH,
    CartAccessory.BLUETOOTH_SPEAKER,
    CartAccessory.CABLE,
    CartAccessory.CAMERA_ACCESSORY,
    CartAccessory.CAR_CHARGER,
    CartAccessory.CAR_SUPPORT,
    CartAccessory.CHARGER,
    CartAccessory.HEADPHONES,
    CartAccessory.OTHER,
    CartAccessory.POWER_BANK,
    CartAccessory.PROTECTOR,
    CartAccessory.SCREEN_PROTECTOR,
    CartAccessory.SELFIE_STICK,
    CartAccessory.TRIPOD,
    CartAccessory.VIDEO_GAME_CONTROLLER,
  ];
});

const accessoryTypePlaceholder = t("CART_ACCESSORY_SELECT_PLACEHOLDER");
const removeLabel = t("CART_ACCESSORY_REMOVE");
const accessoryTitle = t("CART_ACCESSORY_TITLE");
const accessoryPriceLabel = t("CART_ACCESSORY_PRICE");

const showAccessories = ref(false);
const isExpanded: Ref<boolean> = ref(false);
const accessoryType = ref("");
const disableAccessoryPricing = ref(true);
const accessoryRemovable = ref(false);
const isAccessoryRemoved = ref(false);
const accessoryPriceField = ref();
const selectAccessoryKey = ref(0);
const accessoryRetailPrice = ref(0);
const cartAccessoryPrice = ref();

const deviceRetailPrice = computed(() => {
  return props.devicePrice || 0;
});
const isSelfOnboarding = computed(() => {
  return (
    store.state.carts?.productVersion === ProductVersion.M3_SELF_ONBOARDING
  );
});
const dropdownLabelClass = computed(() => {
  return isSelfOnboarding.value ? "self-onboarding-label-font-size" : "";
});

const accessoriesBoxToggle = () => {
  isExpanded.value = !isExpanded.value;
};

const setAccessoryRemoved = () => {
  accessoryRemovable.value = false;
  if (accessoryPriceField.value) {
    accessoryPriceField.value.setInputPrice("");
  }
  store.dispatch("offers/setSelectedCartAccessory", {});
  accessoryType.value = "";
  selectAccessoryKey.value += 1;
  disableAccessoryPricing.value = true;
  accessoryRetailPrice.value = 0;
  cartAccessoryPrice.value = 0;
  isAccessoryRemoved.value = true;

  updatePrice();
  emitAccessory();
};
const accessoriesToggleIconClass = computed(() => {
  return isExpanded.value ? "fa fa-circle-minus" : "fa fa-circle-plus";
});

const setAccessoryType = (value: IOptionDropdown) => {
  if (value.code == "") {
    disableAccessoryPricing.value = true;
    showAccessories.value = false;
    return;
  }

  disableAccessoryPricing.value = false;
  accessoryRemovable.value = true;
  showAccessories.value = true;
  accessoryType.value = value.code;
  isAccessoryRemoved.value = false;

  updatePrice();
  emitAccessory();
};

const accessoryErrorMessage = ref("");

const setAccessoryPrice = (value: number) => {
  isAccessoryRemoved.value = value <= 0;
  accessoryRetailPrice.value = value;
  accessoryRemovable.value = true;
  updatePrice();
  emitAccessory();
};

const updatePrice = () => {
  const cartAccessory: ICartAccessory = {
    type: accessoryType.value,
    isRemoved: isAccessoryRemoved.value,
  };
  store.dispatch("offers/setSelectedCartAccessory", cartAccessory);

  if (accessoryRetailPrice.value === 0 || !deviceRetailPrice.value) {
    return;
  }

  const maxAccessoryCartPercentage = 0.3;
  const maxAccessoryPrice =
    deviceRetailPrice.value * maxAccessoryCartPercentage;
  if (accessoryRetailPrice.value <= maxAccessoryPrice) {
    const cartAccessory: ICartAccessory = {
      type: accessoryType.value,
      price: accessoryRetailPrice.value,
      isRemoved: isAccessoryRemoved.value,
    };
    store.dispatch("offers/setSelectedCartAccessory", cartAccessory);
    if (accessoryErrorMessage.value) {
      accessoryErrorMessage.value = "";
    }
  } else {
    accessoryErrorMessage.value = t(
      "ERROR_ACCESSORY_BUNDLE_PRICE_OUT_OF_RANGE"
    );
  }
};

const emitAccessory = () => {
  const cartAccessory: ICartAccessory = {
    type: accessoryType.value,
    price: accessoryRetailPrice.value,
    isRemoved: isAccessoryRemoved.value,
  };

  emit("accessory-update", cartAccessory);
};

watchEffect(() => {
  if (isPhilippinesSaleFlow()) {
    showAccessoryPrice.value = false;
  }
});

onMounted(() => {
  useMerchantPersistState(() => true);
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/_constants.scss";
.accessories_box {
  background-color: $pj_color-light-gray;
  border-radius: 5px;
}
.accessories_box_header {
  color: $pj-color-green;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}
.accessories_box_body {
  transition: all 0.3s ease-out;
}
.remove-accessory-btn {
  border: 0;
  font-size: 12px;
  color: $pj-color-red;
}
.error-msg {
  color: $pj-color-red;
}
</style>
